import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Toast from '../components/Toast';
import { 
  calculateDeliveryPrice, 
  formatPrice, 
  getCommunes 
} from '../services/pricingService';
import { generateUniqueOrderReference } from '../services/orderService';
import '../styles/DeliveryForm.css';

interface FormData {
  description: string;
  deliveryType: 'pickup' | 'dropoff';
  pickupAddress: string;
  pickupCommune: string;
  dropoffAddress: string;
  dropoffCommune: string;
  senderName: string;
  paymentMethod: 'cash' | 'mpesa' | 'orange_money';
  receiverName: string;
  receiverPhone: string;
}

interface FormErrors {
  [key: string]: string;
}

const DeliveryForm: React.FC = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    description: '',
    deliveryType: 'pickup',
    pickupAddress: '',
    pickupCommune: '',
    dropoffAddress: '',
    dropoffCommune: '',
    senderName: '',
    paymentMethod: 'cash',
    receiverName: '',
    receiverPhone: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [estimatedPrice, setEstimatedPrice] = useState<number | null>(null);
  const [pickupPrice, setPickupPrice] = useState<number>(0);
  const [dropoffPrice, setDropoffPrice] = useState<number>(0);
  const [communes, setCommunes] = useState<string[]>([]);
  const [isLoadingCommunes, setIsLoadingCommunes] = useState(true);

  // Load communes from Firebase on component mount
  useEffect(() => {
    const loadCommunes = async () => {
      try {
        const communesData = await getCommunes();
        setCommunes(communesData);
      } catch (error) {
        console.error('Error loading communes:', error);
        // Fallback to hardcoded communes if Firebase fails
        setCommunes([
          "Bandalungwa", "Barumbu", "Bumbu", "Gombe", "Kalamu", "Kasa-Vubu",
          "Kimbanseke", "Kinshasa", "Kintambo", "Kisenso", "Lemba", "Limete",
          "Lingwala", "Makala", "Maluku", "Masina", "Matete", "Mont-Ngafula",
          "Ndjili", "Ngaba", "Ngaliema", "Ngiri-Ngiri", "Nsele", "Selembao"
        ]);
      } finally {
        setIsLoadingCommunes(false);
      }
    };

    loadCommunes();
  }, []);

  // Calculate price whenever relevant fields change
  useEffect(() => {
    const calculatePrices = async () => {
      try {
        // Calculate dropoff price if commune is selected
        if (formData.dropoffCommune) {
          const dropPrice = await calculateDeliveryPrice('dropoff', '', formData.dropoffCommune);
          setDropoffPrice(dropPrice);
        } else {
          setDropoffPrice(0);
        }

        // Calculate pickup price if pickup commune is selected (for pickup type)
        if (formData.deliveryType === 'pickup' && formData.pickupCommune) {
          const pickPrice = await calculateDeliveryPrice('dropoff', '', formData.pickupCommune);
          setPickupPrice(pickPrice);
        } else {
          setPickupPrice(0);
        }

        // Calculate total price
        if (formData.dropoffCommune && (formData.deliveryType === 'dropoff' || formData.pickupCommune)) {
          const totalPrice = await calculateDeliveryPrice(
            formData.deliveryType,
            formData.pickupCommune,
            formData.dropoffCommune
          );
          setEstimatedPrice(totalPrice);
        } else {
          setEstimatedPrice(null);
        }
      } catch (error) {
        console.error('Error calculating prices:', error);
        setEstimatedPrice(null);
        setPickupPrice(0);
        setDropoffPrice(0);
      }
    };

    calculatePrices();
  }, [formData.deliveryType, formData.pickupCommune, formData.dropoffCommune]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.description.trim()) {
      newErrors.description = 'La description est requise';
    }
    
    if (formData.deliveryType === 'pickup' && !formData.pickupAddress.trim()) {
      newErrors.pickupAddress = 'L\'adresse de collection est requise';
    }
    
    if (formData.deliveryType === 'pickup' && !formData.pickupCommune) {
      newErrors.pickupCommune = 'La commune de collection est requise';
    }
    
    if (!formData.dropoffAddress.trim()) {
      newErrors.dropoffAddress = 'L\'adresse de livraison est requise';
    }
    
    if (!formData.dropoffCommune) {
      newErrors.dropoffCommune = 'La commune de livraison est requise';
    }
    
    if (!formData.senderName.trim()) {
      newErrors.senderName = 'Le nom de l\'expéditeur est requis';
    }
    
    if (!formData.paymentMethod) {
      newErrors.paymentMethod = 'Le mode de paiement est requis';
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      // Find first error field and scroll to it
      const firstErrorField = Object.keys(newErrors)[0];
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
      
      setToastMessage('Veuillez remplir tous les champs obligatoires');
      setShowToast(true);
      
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log('=== DEBUGGING DELIVERY SUBMISSION ===');
      console.log('1. Starting order submission...');
      
      // Generate unique order reference before creating the order
      console.log('2. Generating unique order reference...');
      const orderReference = await generateUniqueOrderReference();
      console.log('3. Order reference generated:', orderReference);
      
      // Prepare order data
      const orderData = {
        ...formData,
        orderReference,
        deliveryCost: estimatedPrice,
        createdAt: new Date(),
        status: 'pending'
      };
      
      console.log('4. Order data prepared:', orderData);
      console.log('5. Attempting to create delivery document...');
      
      // Create the order with the unique reference
      const docRef = await addDoc(collection(db, 'deliveries'), orderData);
      
      console.log('6. Delivery created successfully with ID:', docRef.id);
      
      navigate('/confirmation', { 
        state: { 
          orderId: docRef.id,
          orderReference,
          paymentMethod: formData.paymentMethod 
        } 
      });
    } catch (error: any) {
      console.error('=== ERROR DETAILS ===');
      console.error('Error type:', error.constructor.name);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Full error object:', error);
      console.error('Stack trace:', error.stack);
      
      setToastMessage('Une erreur est survenue. Veuillez réessayer.');
      setShowToast(true);
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  return (
    <>
      {showToast && (
        <Toast
          message={toastMessage}
          type="error"
          onClose={() => setShowToast(false)}
        />
      )}
      <div className="delivery-form-page">
      <button className="back-button" onClick={handleBack}>
        ← Retour
      </button>
      
      <div className="form-container">
        <h1>Nouvelle demande de livraison</h1>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Type de service *</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="deliveryType"
                  value="pickup"
                  checked={formData.deliveryType === 'pickup'}
                  onChange={handleChange}
                />
                <span>Collection et livraison</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="deliveryType"
                  value="dropoff"
                  checked={formData.deliveryType === 'dropoff'}
                  onChange={handleChange}
                />
                <span>Dépôt seulement (je dépose le colis)</span>
              </label>
            </div>
          </div>

          <div className={`form-group ${errors.description ? 'error' : ''}`}>
            <label htmlFor="description">Description des articles à livrer *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Décrivez les articles à livrer..."
              rows={4}
              className={errors.description ? 'input-error' : ''}
            />
            {errors.description && <span className="error-message">{errors.description}</span>}
          </div>

          {formData.deliveryType === 'pickup' && (
            <>
              <div className={`form-group ${errors.pickupCommune ? 'error' : ''}`}>
                <label htmlFor="pickupCommune">Commune de collection *</label>
                <select
                  id="pickupCommune"
                  name="pickupCommune"
                  value={formData.pickupCommune}
                  onChange={handleChange}
                  className={errors.pickupCommune ? 'input-error' : ''}
                >
                  <option value="">
                    {isLoadingCommunes ? 'Chargement...' : 'Sélectionnez une commune'}
                  </option>
                  {communes.map(commune => (
                    <option key={commune} value={commune}>{commune}</option>
                  ))}
                </select>
                {errors.pickupCommune && <span className="error-message">{errors.pickupCommune}</span>}
              </div>

              <div className={`form-group ${errors.pickupAddress ? 'error' : ''}`}>
                <label htmlFor="pickupAddress">Adresse de collection *</label>
                <input
                  type="text"
                  id="pickupAddress"
                  name="pickupAddress"
                  value={formData.pickupAddress}
                  onChange={handleChange}
                  placeholder="Ex: Avenue du Commerce 123"
                  className={errors.pickupAddress ? 'input-error' : ''}
                />
                {errors.pickupAddress && <span className="error-message">{errors.pickupAddress}</span>}
              </div>
            </>
          )}

          <div className={`form-group ${errors.dropoffCommune ? 'error' : ''}`}>
            <label htmlFor="dropoffCommune">Commune de livraison *</label>
            <select
              id="dropoffCommune"
              name="dropoffCommune"
              value={formData.dropoffCommune}
              onChange={handleChange}
              className={errors.dropoffCommune ? 'input-error' : ''}
            >
              <option value="">
                {isLoadingCommunes ? 'Chargement...' : 'Sélectionnez une commune'}
              </option>
              {communes.map(commune => (
                <option key={commune} value={commune}>{commune}</option>
              ))}
            </select>
            {errors.dropoffCommune && <span className="error-message">{errors.dropoffCommune}</span>}
          </div>

          <div className={`form-group ${errors.dropoffAddress ? 'error' : ''}`}>
            <label htmlFor="dropoffAddress">Adresse de livraison *</label>
            <input
              type="text"
              id="dropoffAddress"
              name="dropoffAddress"
              value={formData.dropoffAddress}
              onChange={handleChange}
              placeholder="Ex: Avenue Kasa-Vubu 456"
              className={errors.dropoffAddress ? 'input-error' : ''}
            />
            {errors.dropoffAddress && <span className="error-message">{errors.dropoffAddress}</span>}
          </div>

          <div className="form-row">
            <div className={`form-group ${errors.senderName ? 'error' : ''}`}>
              <label htmlFor="senderName">Nom de l'expéditeur *</label>
              <input
                type="text"
                id="senderName"
                name="senderName"
                value={formData.senderName}
                onChange={handleChange}
                placeholder="Votre nom"
                className={errors.senderName ? 'input-error' : ''}
              />
              {errors.senderName && <span className="error-message">{errors.senderName}</span>}
            </div>

            <div className={`form-group ${errors.paymentMethod ? 'error' : ''}`}>
              <label htmlFor="paymentMethod">Mode de paiement *</label>
              <select
                id="paymentMethod"
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleChange}
                className={errors.paymentMethod ? 'input-error' : ''}
              >
                <option value="cash">Cash</option>
                <option value="mpesa">M-Pesa: 0822193993</option>
                <option value="orange_money">Orange MONEY: 0842193989</option>
              </select>
              {errors.paymentMethod && <span className="error-message">{errors.paymentMethod}</span>}
            </div>
          </div>

          <div className="optional-section">
            <h3>Informations du destinataire (optionnel)</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="receiverName">Nom du destinataire</label>
                <input
                  type="text"
                  id="receiverName"
                  name="receiverName"
                  value={formData.receiverName}
                  onChange={handleChange}
                  placeholder="Nom du destinataire"
                />
              </div>

              <div className="form-group">
                <label htmlFor="receiverPhone">Téléphone du destinataire</label>
                <input
                  type="tel"
                  id="receiverPhone"
                  name="receiverPhone"
                  value={formData.receiverPhone}
                  onChange={handleChange}
                  placeholder="+243 XXX XXX XXX"
                />
              </div>
            </div>
          </div>

          {estimatedPrice && (
            <div className="price-estimate">
              <h3>Estimation du prix</h3>
              <div className="price-details">
                {formData.deliveryType === 'pickup' && (
                  <>
                    <div className="price-line">
                      <span>Frais de collection ({formData.pickupCommune}):</span>
                      <span>{formatPrice(pickupPrice)}</span>
                    </div>
                    <div className="price-line">
                      <span>Frais de livraison ({formData.dropoffCommune}):</span>
                      <span>{formatPrice(dropoffPrice)}</span>
                    </div>
                    <div className="price-line total">
                      <span>Total estimé:</span>
                      <span>{formatPrice(estimatedPrice)}</span>
                    </div>
                  </>
                )}
                {formData.deliveryType === 'dropoff' && (
                  <div className="price-line total">
                    <span>Frais de livraison ({formData.dropoffCommune}):</span>
                    <span>{formatPrice(estimatedPrice)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <button 
            type="submit" 
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Envoi en cours...' : 'Confirmer la demande'}
          </button>
        </form>
      </div>
    </div>
    </>
  );
};

export default DeliveryForm;